import type { ReactNode } from 'react'
import { useId } from 'react'
import { cx } from './core.tsx'

// -------------------------------------------------------------- FieldBlock

export type FieldBlockProps = {
  label: string
  children: (props: { id: string; invalid: boolean }) => ReactNode
  required?: boolean
  hint?: string
  error?: string | null
}

/**
 * Rotulo + controle + ajuda + erro.
 *
 * `children` recebe o `id` gerado para poder liga-lo ao `<label>`, e o estado
 * `invalid` para o controle se pintar. Sem isso cada tela teria que criar o par
 * id/htmlFor na mao e alguns esqueceriam — o `<label>` orfao e o defeito de
 * acessibilidade mais comum em formulario.
 */
export function FieldBlock({ label, children, required, hint, error }: FieldBlockProps) {
  const id = useId()
  const invalid = Boolean(error)
  return (
    <div className="ob-field">
      <label className="ob-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="ob-field__required" aria-hidden>
            {' '}
            *
          </span>
        ) : null}
      </label>
      {children({ id, invalid })}
      {hint && !error ? <span className="ob-field__hint">{hint}</span> : null}
      {error ? (
        <span className="ob-field__error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden />
          {error}
        </span>
      ) : null}
    </div>
  )
}

// -------------------------------------------------------------- PageLayout

export type PageLayoutProps = {
  title: string
  subtitle?: string
  breadcrumbs?: ReactNode
  actions?: ReactNode
  tabs?: ReactNode
  filters?: ReactNode
  children: ReactNode
  className?: string
}

/**
 * Estrutura padrao de pagina da spec 7.3, na ordem exigida:
 * breadcrumbs, titulo+subtitulo, acoes, tabs, filtros, conteudo.
 *
 * Todas as telas passam por aqui para que a ordem nao divirja entre modulos.
 */
export function PageLayout({
  title,
  subtitle,
  breadcrumbs,
  actions,
  tabs,
  filters,
  children,
  className,
}: PageLayoutProps) {
  return (
    <div className={cx('ob-page', className)}>
      {breadcrumbs}
      <header className="ob-page__header">
        <div>
          <h1 className="ob-page__title">{title}</h1>
          {subtitle ? <p className="ob-page__subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="ob-page__actions">{actions}</div> : null}
      </header>
      {tabs}
      {filters}
      <main className="ob-stack">{children}</main>
    </div>
  )
}
